import { CentPrecisionMoney, TypedMoney } from "@commercetools/platform-sdk"
import { Maybe } from "@jog/ct"
import { Facet, type JoggOrder, type JoggStore, TweakwiseItem } from "@jog/types"
import { asText } from "@prismicio/helpers"
import { BooleanField, RichTextField, TitleField } from "@prismicio/types"
import { parsePhoneNumber } from "awesome-phonenumber"
import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import Router from "next/router"

import { DUTCH_WEEK } from "./const"
import { localStorage } from "./localStorage"
import { RedisItem } from "./redis/type"
import { PrismicTextConversionJson } from "./scripts"

dayjs.extend(isBetween)

export const sleep = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds))

const addZeroIfCentsAreLessThanTen = (cents: number) => {
    if (cents < 10) return "0" + cents
    return cents
}
export const convertCentsToEuros = (cents: number | string | undefined | null) => {
    cents = Math.round(Number(cents)) || 0
    const euros = Math.floor(cents / 100)
    const remainingCents = addZeroIfCentsAreLessThanTen(cents % 100)

    if (remainingCents === 0) {
        return `${euros}`
    } else {
        const separator = ["FR", "DE"].includes(process.env.NEXT_PUBLIC_COUNTRY || "") ? "," : "."
        return `${euros}${separator}${remainingCents}`
    }
}

type FalseType = "" | 0 | false | null | undefined

export const typedBoolean = <Value>(value: Value): value is Exclude<Value, FalseType> => Boolean(value)

export const parsePhoneNumberByCountryCode = (phone: string | undefined, country?: string) => {
    if (!phone) return ""
    const phoneNumber = parsePhoneNumber(phone, { regionCode: country })
    return phoneNumber.valid ? phoneNumber.number.national?.replace(/-|\s/g, "") : ""
}

export const navigate = (pathname: string, search?: string, query?: Record<string, any>) => {
    if (typeof window !== "undefined") {
        Router.push({ pathname, search, query }).catch(() => {})
    }
}

const ERROR_MESSAGE_MAP = {
    cancel: "You have completed the payment process",
    reject: "The payment was declined",
    error: "The payment failed",
    orderCallFail: "Something bad has happened. Order ID is incorrect or incomplete",
    dummyShippingMethodSet: "Shipping method was not set correctly. Please set it again",
    shippingMethodMissing: "Shipping method was not set correctly. Please set it again",
    DEFAULT: "Something went wrong",
    captchaFail: "Captcha validation was not successful",
} as const

export const returnCorrectErrorMessage = (errorType: keyof typeof ERROR_MESSAGE_MAP) =>
    ERROR_MESSAGE_MAP[errorType] || ERROR_MESSAGE_MAP.DEFAULT

export const checkForTimeElapsed = (storageItem: string, maxDays: number) => {
    const today = dayjs().toDate()
    const lastPrompt = dayjs(localStorage.getItem(storageItem))
    const days = dayjs(today).diff(lastPrompt, "days")
    return isNaN(days) || days > maxDays
}

export const shouldShowPopupModal = () => checkForTimeElapsed("popupModal", 7)

export const shouldShowWishlistModal = () => checkForTimeElapsed("wishlistModal", 7)

export const setDisplayTime = (storageItem: string) => localStorage.setItem(storageItem, dayjs().toDate())

export const setPopupModalDisplayTime = () => setDisplayTime("popupModal")

export const setCountryRedirectModalDisplayTime = () => setDisplayTime("countryRedirectModal")

export const setWishlistModalTime = () => setDisplayTime("wishlistModal")

export const getIsCmsViewDeliveryOptionsEnabled = (
    deliveryOptions: { develiry_name: TitleField; is_delivery_enabled: BooleanField }[],
    deliveryName: string
) => deliveryOptions?.find((item) => asText(item.develiry_name) === deliveryName)?.is_delivery_enabled

export const getSelectedAttributes = (facet: Facet, pageUrl: string) => {
    const { selectiontype, urlkey } = facet.facetsettings
    const attributes = facet.attributes
        .filter((attribute) => attribute.isselected)
        .map(({ title, url }) => ({
            title,
            url:
                selectiontype === "slider"
                    ? pageUrl
                          .split("&")
                          .filter((param) => !param.includes(`tn_fk_${urlkey}`))
                          .join("&")
                    : url,
        }))
    return attributes.length ? { selectiontype, attributes } : null
}
export const getBaseCategoryFromPrettyPath = (prettyPath: string) => {
    // We need to extract the base category path from the pretty paths that include one or more facets
    // e.g. `dames/jeans/merk_garcia/kleur_blauw` and `dames/jeans/maat_104/110` should return `dames/jeans`
    const hasSlashAfterFirstFacet = prettyPath.indexOf("_") > 0 && prettyPath.indexOf("_") < prettyPath.lastIndexOf("/")

    return hasSlashAfterFirstFacet
        ? prettyPath.split("_")[0].split("/").slice(0, -1)
        : prettyPath.split("/").filter((e) => !e.includes("_"))
}

export const getRobotsMetaTagBase = (facetsToIndex: string[], pagesToDisallow: string[]) => (path: string) => {
    // Detect pages that are blacklisted in Robots config in Prismic
    const pathContainsFacetsToDisallow = pagesToDisallow.some((path_wildcard) => path.includes(path_wildcard))

    // Detect if path is regular or pretty
    const isPrettyPath = path.indexOf("_") > 0

    // Detect if the (pretty) path contains facets that should be indexed (taken from a Prismic list)
    const pathContainsFacetsToIndex = facetsToIndex.map((f) => f + "_").some((facet) => path.includes(facet))

    // Detect if path contains multiple facets with special edge case handling for the Size facet case:
    // The "/" in the Size facet value is a special case that should be handled
    // The following regex tests for "_digit/digit" or "_digit/digit-digit/digit" at the end of a string
    // true  -> /dames/jeans/maat_28/32
    // true  -> /dames/jeans/maat_28/32-28/34
    // true  -> /dames/jeans/kleur_denim_blauw/maat_28/32
    // true  -> /dames/jeans/kleur_denim_blauw/maat_28/32-28/34
    // false -> /dames/jeans/maat_28/32/kleur_denim_blauw
    // false -> /dames/jeans/maat_28/32-28/34/kleur_denim_blauw
    const regex = /_(\d+\/\d+|\d+\/\d+-\d+\/\d+)$/
    const lastFacetIsSize = regex.test(path)

    // Auxiliary bools
    const pathHasMoreThanOneUnderscore = path.indexOf("_") !== path.lastIndexOf("_")
    const pathHasSlashAfterFirstUnderscore = path.indexOf("_") < path.lastIndexOf("/")
    const pathLastUnderscoreIsAfterLastSlash = path.lastIndexOf("_") > path.lastIndexOf("/")
    const pathLastSlashIsAfterLastUnderscore = path.lastIndexOf("_") < path.lastIndexOf("/")

    // `/dames/jeans/skinny-fit/kleur_denim_blauw`             is false (one facet)
    // `/dames/jeans/skinny-fit/kleur_denim_blauw/merk_garcia` is true  (two facets)
    const pathContainsMultipleFacets =
        pathHasMoreThanOneUnderscore &&
        pathHasSlashAfterFirstUnderscore &&
        // The last underscore is after the last slash
        (pathLastUnderscoreIsAfterLastSlash ||
            // or there is a slash after the last underscore and the last facet is Size (special case)
            (pathLastSlashIsAfterLastUnderscore && lastFacetIsSize))

    // Detect if path contains any facet(s) with multiple values
    const pathContainsFacetWithMultipleValues = path.lastIndexOf("-") && path.indexOf("_") < path.lastIndexOf("-")

    // Evaluate whether page should be indexed or not
    const shouldIndex =
        // 1. If page is blacklisted via Robots config in Prismic, disallow indexing
        !pathContainsFacetsToDisallow &&
        // 2. If normal category path (no filters), index the page
        (!isPrettyPath ||
            // 3. If it's a pretty path, only index the page if it obeys the rules stated in JOGGMNT-869
            // https://fyayc-am.atlassian.net/browse/JOGGMNT-869
            (pathContainsFacetsToIndex && !pathContainsMultipleFacets && !pathContainsFacetWithMultipleValues))

    return shouldIndex ? "index, follow" : "noindex, nofollow"
}

declare global {
    interface Navigator {
        standalone: boolean
    }
}
export const checkForIOS = () => {
    if (typeof window === "undefined" || window.navigator.standalone) {
        return false
    }

    const ua = window.navigator.userAgent
    const isIPad = !!ua.match(/iPad/i)
    const isIPhone = !!ua.match(/iPhone/i)
    return isIPad || isIPhone
}

export const retry = async <T>(fn: () => Promise<T>, n = 3, sleepTime = 500): Promise<T> => {
    try {
        return fn()
    } catch (e) {
        if (n === 0) {
            console.log(`Retry fail`)
            throw e
        } else {
            console.log(e)
            console.log(`Retry it, sleep ${sleepTime}ms, the rest time ${n}`)
            await sleep(sleepTime)
            return retry<T>(fn, n - 1)
        }
    }
}

export const colorLanguageConverter = (color: string, productColorMap: Record<string, string>) => {
    const _color = (color || "").toLowerCase()
    return productColorMap[_color] || productColorMap.default
}

export const textConvert = (text: string = "", textConversionMap: PrismicTextConversionJson[]) => {
    let convertedText = text
    textConversionMap.forEach((item) => {
        const regExp = new RegExp(item.initValue, "gi")
        convertedText = convertedText?.replace(regExp, item.convertedValue)
    })
    return convertedText
}

export const getDomain = () => {
    const site = (process.env.NEXT_PUBLIC_SITE_URL || "").replace(/\/*$/, "")
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "preview") {
        return process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL ? `https://${process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL}` : site
    }
    return site
}

const PRE_BUILD_PAGE_TYPE_DEFAULT: RedisItem["type"][] = ["category", "service", "market"]
export const getPreBuildPageType = (): RedisItem["type"][] => {
    const PRE_BUILD_PAGE_TYPE = process.env.PRE_BUILD_PAGE_TYPE || ""
    if (!PRE_BUILD_PAGE_TYPE) return PRE_BUILD_PAGE_TYPE_DEFAULT
    try {
        return PRE_BUILD_PAGE_TYPE.split(",") as RedisItem["type"][]
    } catch {
        return PRE_BUILD_PAGE_TYPE_DEFAULT
    }
}

export const handleCartError = async <T extends (...p: any[]) => any>(
    fn: T,
    version: number,
    n = 5
): Promise<ReturnType<T>> => {
    try {
        return await fn(version)
    } catch (error: any) {
        if (error.statusCode === 400 && error?.body?.errors?.[0]?.message === "The cart is not in active state.") {
            localStorage.removeItem("createdCart")
            window.location.reload()
            throw error
        }
        if (error.statusCode === 404 && error?.body?.errors?.[0]?.code === "ResourceNotFound") {
            localStorage.removeItem("createdCart")
            window.location.reload()
            throw error
        }
        if (error.statusCode === 409 && n > 0 && error?.body?.errors?.[0].currentVersion) {
            const versionBody = error?.body?.errors.find(
                (item: { code: string; currentVersion: number }) => item.currentVersion
            )
            if (versionBody) {
                console.log(`version mismatched, the rest time ${n}`)
                return handleCartError<T>(fn, error.body.errors[0].currentVersion as number, n - 1)
            } else {
                throw error
            }
        } else {
            throw error
        }
    }
}

export const NOT_FOUND = () =>
    ({
        revalidate: getRevalidateTime(),
        notFound: true,
    }) as const
export const isAssetPath = (slug: string) => /(\.js|\.txt|\.css|\.jpg|\.png|\.svg|\.ico|\.xml|\.json)$/.test(slug)

export const normalizePdpImages = (
    images: { url: string; label?: Maybe<string> }[],
    sorting_by_name: string[],
    sorting_by_quality: string[]
) => {
    type Name = string
    type Quality = string
    const group = images.reduce<Record<Name, Record<Quality, { url: string; label: string }>>>((pre, cur) => {
        if (!cur.label) return pre
        const labels = cur.label.split("-")
        const name = labels.slice(0, -1).join("-")
        const quality = labels.at(-1)!
        pre[name] = pre[name] || {}
        pre[name][quality] = { label: cur.label, url: cur.url }
        return pre
    }, {})
    const sortingByName = [...sorting_by_name].reverse()
    const sortingByQuality = [...sorting_by_quality].reverse()
    const sortingByQualityOrder = ["2000", "1000", "500"]
    const groupAndSortByName = Object.entries(group)
        .map(([name, qualityGroup]) => {
            const index = sortingByName.findIndex((i) => i === name)
            return { qualityGroup, name, index }
        })
        .sort((a, b) => b.index - a.index)
    return groupAndSortByName
        .map(({ qualityGroup }) => {
            if (Object.keys(qualityGroup).length === 0) return null
            const groupByQuality = Object.entries(qualityGroup).map(([quality, image]) => {
                const index = sortingByQuality.findIndex((i) => i === quality)
                const qualityOrder = sortingByQualityOrder.findIndex((i) => i === quality)
                return { image, index, qualityOrder }
            })
            const priority = groupByQuality.sort((a, b) => b.index - a.index).at(0)!
            const lowest = groupByQuality.sort((a, b) => b.qualityOrder - a.qualityOrder).at(0)!
            const highest = groupByQuality
                .filter((i) => i.qualityOrder > -1)
                .sort((a, b) => a.qualityOrder - b.qualityOrder)
                .at(0)!
            return {
                ...priority.image,
                lowest: lowest.image?.url || "",
                highest: highest.image?.url || "",
            }
        })
        .filter(typedBoolean)
        .filter((i) => i.url)
}

export const transGtmId = ({
    articleName,
    colorCode,
}: {
    articleName: string | undefined
    colorCode: string | undefined
}) => [articleName, colorCode].filter(typedBoolean).join("_").replace(/ /g, "_")

export const getGtmIdFromCT = (
    attributes?: { name: string; value: string | number | TypedMoney | Record<string, string> }[]
) => {
    const articleName = (attributes?.find((i) => i.name === "articlename")?.value || "") as string
    const colorCode = (attributes?.find((i) => i.name === "colorcode")?.value || "") as string
    return transGtmId({ articleName: articleName, colorCode: colorCode })
}
export const getArticleNameFromTW = (product: TweakwiseItem) => {
    const nameAndColorCode = product.attributes.find((i) => i.name === "nameAndColorCode")?.values?.[0]
    if (!nameAndColorCode) return null
    try {
        return JSON.parse(nameAndColorCode) as {
            articleName: string
            colorCode: string
        }
    } catch (e) {
        console.error(e)
        return null
    }
}
export const getGtmIdFromTW = (product: TweakwiseItem) => {
    const data = getArticleNameFromTW(product)
    if (!data) return ""
    return transGtmId(data)
}

export const JC_FRIEND_SKU = "2400059866011"

export const getRevenue = (order: JoggOrder) => {
    const shipping = parseFloat(convertCentsToEuros(order.shippingCosts?.costs.centAmount))
    const tax = parseFloat(convertCentsToEuros(order.totalTax?.centAmount))
    const revenue = parseFloat(convertCentsToEuros(order.subtotal.centAmount - order.totalDiscount.centAmount))
    return {
        shipping,
        tax,
        revenue,
    }
}

export const replaceTextInRichText = (rawText: RichTextField, searchValue: string, replaceValue: string) => {
    try {
        if (rawText) {
            return JSON.parse(JSON.stringify(rawText).replace(searchValue, replaceValue)) as RichTextField
        }
    } catch (error) {
        console.log(error, "parse rich text error")
    }
    return rawText
}

export const getRevalidateTime = () => {
    const NEXT_REVALIDATE_TIME = +(process.env.NEXT_REVALIDATE_TIME || "")
    return NEXT_REVALIDATE_TIME || 60 * 20
}

export const replacePlaceholderInUrl = (url: string) => {
    return url.replace(/https:\/\/(\{site}|\/?\$)/, "")
}

export const lowerCaseEq = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

export const fetchResponse = async <Data = any>(response: Response): Promise<Data> => {
    if (response.ok) {
        return (await response.json()) as Data
    }
    const errorBody = await response.text()
    throw new Error(errorBody || response.statusText)
}

export const lowerCaseLocale = () => process.env.NEXT_PUBLIC_LOCALE?.toLowerCase() || ""

export function getDomains(): { locale: string; domain: string }[] {
    try {
        return JSON.parse(process.env.DOMAINS || "[]")
    } catch {
        return []
    }
}

export function getPaths(
    attributes?: Maybe<{ name: string; value: string | number | CentPrecisionMoney | Record<string, string> }[]>
): { locale: string; url: string }[] {
    if (!attributes) throw new Error("attributes empty")
    const domains = getDomains()
    const paths = (attributes.find((i) => i.name === "path")?.value as Record<string, string>) || {}
    paths["en-US"] = paths["en-US"] || paths["en"]
    return domains
        .map(({ locale, domain }) => {
            const path = paths[locale]
            if (!path) return
            return { locale, url: [domain.replace(/\/$/, ""), path.replace(/^\//, "")].join("/") }
        })
        .filter(typedBoolean)
}

export const dateTimeFormat = (date: string | number, options?: Parameters<typeof Intl.DateTimeFormat>[1]) => {
    return new Intl.DateTimeFormat(lowerCaseLocale(), {
        year: "numeric",
        month: "short",
        day: "numeric",
        ...options,
    }).format(new Date(date))
}

export const centFormat = (cents: number) => +(cents / 100).toFixed(2)

export const euroFormat = (money: string) => {
    if (["FR", "DE"].includes(process.env.NEXT_PUBLIC_COUNTRY || "")) return `${money} €`
    return `€${money}`
}

export const getOpeningHoursSpecification = (openings: JoggStore["openings"]) =>
    DUTCH_WEEK.map((dayOfWeek) => {
        const [opens, closes] = openings[dayOfWeek].split("-").map((i) => i.trim())
        return { dayOfWeek, opens, closes }
    }).reduce<
        {
            opens: string
            closes: string
            dayOfWeek: (typeof DUTCH_WEEK)[number][]
            ["@type"]: "OpeningHoursSpecification"
        }[]
    >((pre, cur) => {
        //exclude remarks key from the array and exclude days without opening and closing hours
        if (!cur.opens || !cur.closes) return pre
        const daysSameTime = pre.find((item) => item.opens === cur.opens && item.closes === cur.closes)
        if (!daysSameTime) {
            pre.push({ ...cur, dayOfWeek: [cur.dayOfWeek], "@type": "OpeningHoursSpecification" })
            return [...pre]
        }
        daysSameTime.dayOfWeek.push(cur.dayOfWeek)
        return [...pre]
    }, [])

export const replaceText = (origin: string | undefined, replace: Record<string, string> = {}) =>
    origin?.replace(/{([^}]+)}/g, (ori, k) => replace[k] || ori)
